import { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { SignIn } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { useAuthContext } from "@/contexts/AuthContext";
import { getSafePostLoginPath, toFullPath, type PathLike } from "@/utils/authRedirect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { authApi } from "@/api";
import { isOwnerAccountLoginError } from "@/api/loginError";

const SHOW_LEGACY_LOGIN = import.meta.env.VITE_SHOW_LEGACY_LOGIN === "true";

// 传统登录表单验证 — messages are backend-facing, not user-visible via FormMessage
const loginSchema = z.object({
  account: z.string().min(1, "Account is required"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  remember: z.boolean().optional(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

/**
 * 传统登录表单组件
 */
function LegacyLoginForm() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { refreshAuth, clerkLoadFailed } = useAuthContext();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      account: "",
      password: "",
      remember: false,
    },
  });

  const onSubmit = async (values: LoginFormValues) => {
    setIsLoading(true);
    setError(null);
    try {
      // 调用传统登录 API，token 会自动存入 localStorage
      await authApi.login({
        username: values.account,
        password: values.password,
      });
      // 通知 AuthContext 重新检查认证状态
      // AuthContext 会检测到 legacy JWT，设置 AUTHENTICATED，AuthGuard 自动跳转到 dashboard
      await refreshAuth();
      setIsLoading(false);
    } catch (err: any) {
      let errorMessage = t("login.legacy.defaultError");

      if (isOwnerAccountLoginError(err)) {
        // 业主门户账号登员工 web：密码是对的，只是走错了入口——说清楚去哪登，
        // 别显示「用户名或密码错误」让人反复试密码
        errorMessage = t("login.legacy.ownerAccount");
      } else if (err.response) {
        const { status, data } = err.response;
        // 优先使用API返回的错误信息字段
        if (data?.msg || data?.message) {
          errorMessage = data.msg || data.message;
        } else {
          switch (status) {
            case 401:
              errorMessage = t("login.legacy.error401");
              break;
            case 422:
              errorMessage = t("login.legacy.error422");
              break;
            case 500:
              errorMessage = t("login.legacy.error500");
              break;
            default:
              errorMessage = data?.detail || errorMessage;
          }
        }
      } else if (err.request) {
        errorMessage = t("login.legacy.networkError");
      } else {
        errorMessage = err.message || errorMessage;
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full border-0 shadow-none bg-white">
      {!clerkLoadFailed && (
        <CardHeader className="space-y-1 text-center px-0 pt-0">
          <CardTitle className="text-2xl font-bold text-gray-900">
            {t("login.legacy.title")}
          </CardTitle>
          <CardDescription className="text-gray-500 text-base">
            {t("login.legacy.subtitle")}
          </CardDescription>
        </CardHeader>
      )}
      <CardContent className="px-0 pb-0">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <fieldset disabled={isLoading} className="contents">
              <div className={isLoading ? "space-y-5 opacity-60 pointer-events-none" : "space-y-5"}>
                <FormField
                  control={form.control}
                  name="account"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="account" className="text-gray-700 font-medium">{t("login.legacy.accountLabel")}</FormLabel>
                      <FormControl>
                        <Input
                          id="account"
                          type="text"
                          placeholder={t("login.legacy.accountPlaceholder")}
                          autoComplete="off"
                          spellCheck={false}
                          className="h-11 rounded-lg border-gray-300 focus:border-[#3A4B76] focus:ring-[#3A4B76]/20"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="password" className="text-gray-700 font-medium">{t("login.legacy.passwordLabel")}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            placeholder={t("login.legacy.passwordPlaceholder")}
                            autoComplete="off"
                            spellCheck={false}
                            className="h-11 rounded-lg border-gray-300 focus:border-[#3A4B76] focus:ring-[#3A4B76]/20"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent cursor-pointer"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? t("login.legacy.hidePassword") : t("login.legacy.showPassword")}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-400" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex items-center justify-between">
                  <FormField
                    control={form.control}
                    name="remember"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                        <FormControl>
                          <input
                            type="checkbox"
                            id="remember"
                            className="w-4 h-4 rounded border-gray-300 focus:ring-2 cursor-pointer"
                            style={{ accentColor: '#3A4B76' }}
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        </FormControl>
                        <FormLabel htmlFor="remember" className="text-sm font-normal text-gray-600 cursor-pointer">
                          {t("login.legacy.rememberMe")}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                  <button
                    type="button"
                    className="text-sm font-medium select-none cursor-pointer hover:underline"
                    style={{ color: '#3A4B76' }}
                    onClick={() => navigate("/forgot-password")}
                  >
                    {t("login.legacy.forgotPassword")}
                  </button>
                </div>
                {error && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}
                <Button
                  type="submit"
                  className="w-full h-11 text-white font-semibold rounded-lg shadow-sm transition-colors duration-200 cursor-pointer"
                  style={{ backgroundColor: '#3A4B76' }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    t("login.legacy.loginBtn")
                  )}
                </Button>
              </div>
            </fieldset>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

/**
 * Clerk 登录组件
 */
function ClerkLoginForm() {
  // 计算登录后回跳目标。
  // 必须用 forceRedirectUrl 覆盖 Clerk dashboard 配置的默认 Home URL，
  // 否则 Clerk 在登录成功后会自己 routerPush 到默认地址，AuthGuard 都来不及处理。
  // 优先级：
  // 1. URL ?from=（401 整页刷新 / AuthGuard 拦截场景写入，能活过 Clerk 整页跳转）
  // 2. location.state.from（同进程内的路由拦截兜底）
  // 3. /dashboard（默认）
  // ⚠️ state 兜底要取 pathname+search+hash：只取 pathname 会把深链参数
  //    （/intents?intentId=123）丢掉，登录后落到空列表页。
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const fromQuery = searchParams.get("from");
  const fromState = toFullPath((location.state as { from?: PathLike } | null)?.from);
  const redirectTarget =
    getSafePostLoginPath(fromQuery) ||
    getSafePostLoginPath(fromState) ||
    "/dashboard";

  return (
    <SignIn
      signUpUrl="/register"
      forceRedirectUrl={redirectTarget}
      appearance={{
        elements: {
          rootBox: "w-full",
          card: "shadow-none rounded-2xl bg-white",
          headerTitle: "text-2xl font-bold text-gray-900",
          headerSubtitle: "text-gray-500",
          formFieldLabel: "text-gray-700 font-medium",
          formFieldInput:
            "h-11 border-gray-300 focus:border-[#3A4B76] focus:ring-[#3A4B76] rounded-lg",
          formButtonPrimary:
            "h-11 bg-[#3A4B76] hover:bg-[#2E3C60] text-white font-medium rounded-lg",
          formButtonReset: "text-[#3A4B76] hover:text-[#2E3C60]",
          footerActionLink: "text-[#3A4B76] hover:text-[#2E3C60] hover:underline",
          socialButtonsBlockButton: "border-gray-300 hover:bg-gray-50",
          dividerLine: "bg-gray-200",
          dividerText: "text-gray-400",
        },
        variables: {
          colorPrimary: "#3A4B76",
          colorText: "#1f2937",
          colorTextSecondary: "#6b7280",
          borderRadius: "0.5rem",
        },
      }}
    />
  );
}

/**
 * 登录页面
 *
 * localdev 模式下同时显示 Clerk 和传统登录（通过 Tab 切换）
 * 其它环境只显示 Clerk 登录
 */
export default function LoginClerkPage() {
  const { t } = useTranslation("auth");
  const { clerkLoadFailed } = useAuthContext();
  const [activeTab, setActiveTab] = useState<"clerk" | "legacy">(
    clerkLoadFailed ? "legacy" : "clerk"
  );

  return (
    <AuthLayout>
      <div className="w-full max-w-[400px]">
        {/* 页面标题 */}
        <div className="mb-6 select-none">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {t("login.welcome")}
          </h1>
          <p className="text-gray-500 text-[15px]">
            {t("login.subtitle")}
          </p>
        </div>

        {/* Tab 切换（Clerk 可用且开启 legacy 模式时显示） */}
        {!clerkLoadFailed && SHOW_LEGACY_LOGIN && (
          <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors duration-200 cursor-pointer ${
                activeTab === "clerk"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("clerk")}
            >
              {t("login.tabClerk")}
            </button>
            <button
              type="button"
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors duration-200 cursor-pointer ${
                activeTab === "legacy"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("legacy")}
            >
              {t("login.tabLegacy")}
            </button>
          </div>
        )}

        {/* 登录表单 */}
        {/* Clerk 不可用时（无 key 或 server 挂了）→ 强制 legacy；否则按 tab/配置决定 */}
        {clerkLoadFailed || activeTab === "legacy" ? (
          <LegacyLoginForm />
        ) : (
          <ClerkLoginForm />
        )}

        {/* 底部文字 */}
        <div className="text-center text-xs text-gray-400 mt-6">
          {t("login.copyright", { year: new Date().getFullYear() })}
        </div>
      </div>
    </AuthLayout>
  );
}
